'use client'

import { useState, type CSSProperties, type ReactNode } from 'react'
import LightningIcon from 'src/app/assets/icons/ico-bolt-lightning.svg'
import './birdie-board.scss'
import { PuttGlyph } from './PuttGlyph'
import { RoundMark } from './RoundMark'
import type { RoundCardDensity, RoundCardView, RoundCardWinner } from './roundCardModel'

/**
 * ONE round, in the capsule's own language — the component the whole of B19
 * hangs off (owner sign-off 2026-09-08 on `birdie-round-card.tpl.html`: "love
 * it, all good").
 *
 * Two densities, one drawing:
 *  - `full` fills the fairness modal, opened from a disc or a sidebar card. It
 *    numbers the three putt slots, because in the modal the ORDER is the thing
 *    the exact market was about.
 *  - `compact` is the history sidebar's row. Same blocks, smaller type, no
 *    numerals — a list is scanned, not read, and three badges per row is noise.
 *
 * What it says, top to bottom: WHICH round (id, and how long ago — or the gold
 * Lightning mark, which takes the same slot so the two can never collide), WHO
 * was putting and how likely they were to hole one, WHAT happened (the three
 * putts in the capsule's slots, with the outcome named beside them), and WHAT
 * IT PAID (both winning options at that round's own committed prices, the
 * boosted one wearing the lightning skin).
 *
 * Everything it says is decided by `roundCardModel.buildRoundCardView`, which
 * is pure and tested; this file decides only how it looks. No translation call
 * is made here — the extractor is literal-only (A25) and every Birdie literal
 * lives in `BirdieRoundCard/birdieCopy.ts`.
 *
 * The gradients the balls are painted with (`<BirdieBallDefs>`) are the MOUNT's
 * to render, once per tree: the modal declares them, and the desktop disc
 * column is inside `<ArcOverlay>`, which already does. A card in a new tree
 * needs one too, or its balls draw unpainted.
 */

type Props = Readonly<{
  view: RoundCardView
  density: RoundCardDensity
  /** When given the whole card is a button that opens the round's modal. */
  onOpen?: () => void
  /** The modal's close cross, rendered inside the card's own padding box. */
  children?: ReactNode
}>

/** The putt slot's ball, px — full density and compact. */
const BALL_PX: Record<RoundCardDensity, number> = { full: 23.7, compact: 19.2 }
/** A winner tile's balls, px. The same at both densities (the template's). */
const WIN_BALL_PX = 12.8
/** The three fixed slot positions. Keys, not indices: a slot never moves. */
const PUTT_SLOTS = ['first', 'second', 'third'] as const

/** The head's two figure icons, from the mockup. Stroked, currentColor. */
function AgeIcon() {
  return (
    <svg aria-hidden="true" className="birdie-rc__fig-icon" viewBox="0 0 24 24">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  )
}

function DistanceIcon() {
  return (
    <svg aria-hidden="true" className="birdie-rc__fig-icon" viewBox="0 0 24 24">
      <path d="M3 17L17 3M6 14l1.5 1.5M9 11l1.5 1.5M12 8l1.5 1.5M15 5l1.5 1.5" />
      <rect height="7" rx="1" width="7" x="2" y="15" />
    </svg>
  )
}

/**
 * The golfer, or their initials on the neutral disc.
 *
 * The FAILED url is tracked rather than a bare boolean, exactly as the capsule
 * and the strip do it: the next round's headshot is retried without an effect.
 * Decorative — the card's own text names the golfer.
 */
function CardAvatar({ view }: Readonly<{ view: RoundCardView }>) {
  const [failedShotUrl, setFailedShotUrl] = useState<string | undefined>(undefined)
  const url =
    view.golfer.headshotUrl !== undefined && view.golfer.headshotUrl !== failedShotUrl
      ? view.golfer.headshotUrl
      : undefined
  return (
    <span className="birdie-rc__avatar">
      {url === undefined ? (
        <span aria-hidden="true" className="birdie-rc__shot birdie-rc__shot--empty">
          {view.golfer.initials}
        </span>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element -- CDN headshot, no loader configured
        <img
          alt=""
          className="birdie-rc__shot"
          onError={() => {
            setFailedShotUrl(view.golfer.headshotUrl)
          }}
          src={url}
        />
      )}
    </span>
  )
}

/** The gold bolt seal on a boosted tile — the board's own, one size down. */
function LightningSeal() {
  return (
    <span aria-hidden="true" className="birdie-rc__seal">
      <LightningIcon />
    </span>
  )
}

/**
 * `wide` comes from the MODEL's winner count, not from CSS's `:only-child`.
 * A lone winner (an all-three-in or none-in round) has a sibling whenever the
 * round also carries a boost that landed on neither option — the third,
 * full-width lightning tile — and the sibling selector stopped matching there,
 * dropping the tile to half width beside an empty column.
 */
function WinnerTile({ winner, wide }: Readonly<{ winner: RoundCardWinner; wide: boolean }>) {
  return (
    <div
      className={`birdie-rc__win birdie-rc__win--${winner.market}${
        winner.lightning ? ' birdie-rc__win--lit' : ''
      }${wide ? ' birdie-rc__win--wide' : ''}`}
    >
      {winner.lightning ? <LightningSeal /> : null}
      <span aria-hidden="true" className="birdie-rc__win-marks">
        {winner.roundType === undefined ? null : (
          // B36: a round-type winner draws its mark where the others draw putt balls.
          <span className={`birdie-rc__win-mark birdie-rc__win-mark--${winner.roundType.toLowerCase()}`}>
            <RoundMark kind={winner.roundType} size={WIN_BALL_PX + 4} />
          </span>
        )}
        {winner.marks.map((mark, index) => (
          // Fixed length, immutable order: the round is settled and a winner's
          // balls cannot change under the key.
          // eslint-disable-next-line react/no-array-index-key -- fixed-length, immutable putt order
          <PuttGlyph key={index} mark={mark} size={WIN_BALL_PX} />
        ))}
      </span>
      <span className="birdie-rc__win-price">
        <span className="birdie-rc__win-paid">{winner.price}</span>
        {winner.basePrice === undefined ? null : (
          <span className="birdie-rc__win-base">{winner.basePrice}</span>
        )}
      </span>
      <span className="birdie-rc__win-tag">{winner.tag}</span>
    </div>
  )
}

export function BirdieRoundCard({ view, density, onOpen, children }: Props) {
  const className = `birdie-rc birdie-rc--${density}${onOpen ? ' birdie-rc--clickable' : ''}`

  const body = (
    <>
      {/* Eyebrow: which round, and when — or, on a boosted round, the gold
          mark. One slot, so a Lightning round never has to fit both. The right
          padding that keeps the modal's close cross off this row is a rule on
          `.birdie-rc--full`, not a value here. */}
      <div className="birdie-rc__eyebrow">
        <span className="birdie-rc__round-id">{view.roundLabel}</span>
        {view.lightning ? (
          <span className="birdie-rc__bolt">
            <LightningIcon />
            {view.lightningLabel}
          </span>
        ) : (
          <span className="birdie-rc__when">{view.timeAgo}</span>
        )}
      </div>

      {/* Head: the capsule's own row — avatar in its gold ring, the name, the
          three figures, and the make chance big in gold on the right. */}
      <div className="birdie-rc__head">
        <CardAvatar view={view} />
        <div className="birdie-rc__who">
          <div className="birdie-rc__name">{view.golfer.name}</div>
          <div className="birdie-rc__figs">
            {view.figures.age === undefined ? null : (
              <span className="birdie-rc__fig">
                <AgeIcon />
                <b>{view.figures.age}</b>
              </span>
            )}
            {view.figures.distance === undefined ? null : (
              <span className="birdie-rc__fig">
                <DistanceIcon />
                <b>{view.figures.distance}</b>
              </span>
            )}
            {view.figures.handicap === undefined ? null : (
              <span className="birdie-rc__hcp">{view.figures.handicap}</span>
            )}
          </div>
        </div>
        <div className="birdie-rc__rate">
          <b>{view.makeChance}</b>
          <span>{view.makeChanceCaption}</span>
        </div>
      </div>

      {/* The result: the three putts in the capsule's slots, the outcome named
          beside them. The numerals are the modal's only — a sidebar row is
          scanned, and three badges per row is noise. */}
      <div className="birdie-rc__result">
        <div aria-hidden="true" className="birdie-rc__slots">
          {PUTT_SLOTS.map((slot, index) => {
            const mark = view.marks[index]
            return (
              <span
                className={`birdie-rc__slot${mark === undefined ? '' : ` birdie-rc__slot--${mark}`}`}
                key={slot}
              >
                {mark === undefined ? null : <PuttGlyph mark={mark} size={BALL_PX[density]} />}
                {density === 'full' ? <i className="birdie-rc__slot-index">{index + 1}</i> : null}
              </span>
            )
          })}
        </div>
        <div className="birdie-rc__outcome">
          <b>{view.outcomeName}</b>
          <span>{view.patternLine}</span>
        </div>
      </div>

      {/* What paid. Two columns; a lone winner (an all-three-in or none-in
          round, whose pattern is settled but never bettable) spans both — and
          it still spans when a missed boost puts a third tile beside it. */}
      <div className="birdie-rc__winners">
        {view.winners.map((winner) => (
          <WinnerTile key={winner.market} wide={view.winners.length === 1} winner={winner} />
        ))}
        {view.lightningMiss === undefined ? null : (
          <div className="birdie-rc__win birdie-rc__win--lit birdie-rc__win--extra">
            <LightningSeal />
            <span aria-hidden="true" className="birdie-rc__win-marks">
              {view.lightningMiss.marks.map((mark, index) => (
                // eslint-disable-next-line react/no-array-index-key -- fixed-length, immutable putt order
                <PuttGlyph key={index} mark={mark} size={WIN_BALL_PX} />
              ))}
            </span>
            <span className="birdie-rc__win-price">
              <span className="birdie-rc__win-paid">{view.lightningMiss.price}</span>
              <span className="birdie-rc__win-base">{view.lightningMiss.basePrice}</span>
            </span>
            <span className="birdie-rc__win-tag">{view.lightningMiss.tag}</span>
          </div>
        )}
      </div>
      {children}
    </>
  )

  // A clickable card is a REAL button, so its accessible name is built from the
  // content a sighted player reads — the golfer, the figures, the outcome and
  // both prices — rather than from a hand-written `aria-label` that would drift
  // from it. The balls are `aria-hidden`; every one of them is named in text.
  if (onOpen) {
    return (
      <button className={className} onClick={onOpen} type="button">
        {body}
      </button>
    )
  }
  return <div className={className}>{body}</div>
}

/**
 * One past round as a 34-px disc in the icon rail (B19 section 1).
 *
 * The rail's own vocabulary, not the card's: the round's make chance on top and
 * its three putts as real balls under it — the two things that compare across
 * rounds that are repriced every round. Nothing on hover but a gold ring; the
 * whole disc opens the modal, where the card tells the round properly.
 *
 * The size arrives as a prop rather than from a class: B19 laid the discs out in
 * the board's own scaled frame, and B19b renders them inside `<ShowRaceResults>`
 * at their reference 34 px instead. Kept as a prop because the ball glyphs are
 * sized off it, so one number decides the whole drawing.
 */
export function BirdieRoundDisc({
  view,
  size,
  onOpen
}: Readonly<{ view: RoundCardView; size: number; onOpen: () => void }>) {
  return (
    <button
      className="birdie-disc"
      onClick={onOpen}
      style={{ '--birdie-disc-size': `${size}px` } as CSSProperties}
      type="button"
    >
      <span aria-hidden="true" className="birdie-disc__rate">
        {view.makeChance}
      </span>
      <span aria-hidden="true" className="birdie-disc__marks">
        {view.marks.map((mark, index) => (
          // eslint-disable-next-line react/no-array-index-key -- fixed-length, immutable putt order
          <PuttGlyph key={index} mark={mark} size={size * 0.2} />
        ))}
      </span>
      {/* The disc's own content is a percentage and three glyphs, so the name
          has to be spelled out. Content, not an `aria-label`: the two are the
          same sentence and only one of them can go stale. */}
      <span className="birdie-sr-only">{view.shortLabel}</span>
    </button>
  )
}
